import * as React from "react"
import "./upload-dropbox.css"
import { get, memoize, isDefined } from "nodash"
import { SimpleSmiley } from "@nova/component-icons"
import { ChangeEvent, MutableRefObject, useCallback, useRef, useState } from "react"
import { DropTargetMonitor, useDrop } from "react-dnd"
import { NativeTypes } from "react-dnd-html5-backend"
import classNames from "classnames"
import { useTranslation } from "@nova/use-translation"

export type FileDropPayload = { files: File[] }

export type NewItemsHandlerType = (files: File[]) => void

type UploadDropBoxProps = {
    allowedFileExtensions: string[]
    newItemsHandler: NewItemsHandlerType
    acceptMultiple: boolean
    resetTrigger?: number | string
    uploadRef?: MutableRefObject<HTMLDivElement | null>
    error?: string
    mapExtensionsToMimeType: {
        [key: string]: string
    }
    theme?: "light" | "dark"
    separator?: string
    dropFileExtensions?: string[]
    externalFileInputRef?: MutableRefObject<HTMLInputElement | null>
    showButton?: boolean
    maxUploads?: number
}

export type CollectedDropProps = { isOver: boolean; canDrop: boolean }
type DropReturnType = void

export const fileAllowableCheck = (
    files: File[],
    allowedFileExtensions: string[],
    mapExtensionsToMimeType: {
        [key: string]: string
    }
): File[] => {
    const allowedFileTypes = allowedFileExtensions
        .map((type) => mapExtensionsToMimeType[type])
        .join(", ")

    if (allowedFileTypes.includes("*")) return files

    allowedFileExtensions = allowedFileExtensions.map((e) => e.toLowerCase())

    return (
        files?.filter((file) => {
            if (file.name.startsWith("._")) return false
            const extension = file.name.split(".").at(-1)?.toLowerCase()
            return (
                (file.type && allowedFileTypes.includes(file.type)) ||
                (extension && allowedFileExtensions.includes(extension))
            )
        }) || []
    )
}

export const dropConfigurationHandler =
    (
        newItemsHandler: NewItemsHandlerType,
        allowedFileExtensions: string[],
        setFileUploadError: (errorState: boolean) => void,
        mapExtensionsToMimeType: {
            [key: string]: string
        },
        dropFileExtensions?: string[],
        maxUploads?: number
    ) =>
    () => ({
        accept: [NativeTypes.FILE],
        canDrop: (item: FileDropPayload) => {
            const files = get(item, "files")
            if (!files) return false
            if (dropFileExtensions)
                allowedFileExtensions = allowedFileExtensions.concat(dropFileExtensions)
            const allowed =
                fileAllowableCheck(files, allowedFileExtensions, mapExtensionsToMimeType) &&
                (maxUploads ? files.length <= maxUploads : true)
            setFileUploadError(!allowed)

            return allowed
        },
        hover: () => {
            setFileUploadError(false)
        },
        drop: (item: FileDropPayload) => {
            const files = get(item, "files")
            if (files.some(isDefined)) {
                newItemsHandler(files)
            }
        },
        collect: (monitor: DropTargetMonitor) => {
            return {
                isOver: monitor.isOver(),
                canDrop: monitor.canDrop(),
            }
        },
    })

export const getInputAcceptAttributeForExtensionsList = memoize((extensions: string[]) =>
    extensions.map((extension) => `.${extension}`).join(", ")
)

/** the two functions below are for reading files from dropped folders */

export type DroppedFile = File & {
    fullPath?: string
}

export async function readAllDirectoryEntries(
    directoryReader: FileSystemDirectoryReader
): Promise<FileSystemEntry[]> {
    const entries: FileSystemEntry[] = []

    async function readChunk(): Promise<void> {
        return new Promise((resolve) => {
            directoryReader.readEntries(async (chunk) => {
                if (chunk.length === 0) {
                    resolve()
                } else {
                    entries.push(...chunk)
                    await readChunk() // keep reading until done
                    resolve()
                }
            })
        })
    }

    await readChunk()
    return entries
}

export async function traverseFileTree(item: FileSystemEntry, path = ""): Promise<File[]> {
    const collectedFiles: File[] = []

    return new Promise((resolve) => {
        if (item.isFile) {
            ;(item as FileSystemFileEntry).file((file: File) => {
                const fullPath = path + file.name
                const fileWithPath = new File([file], file.name, { type: file.type }) as DroppedFile
                fileWithPath.fullPath = fullPath
                collectedFiles.push(fileWithPath)
                resolve(collectedFiles)
            })
        } else if (item.isDirectory) {
            const dirReader = (item as FileSystemDirectoryEntry).createReader()
            readAllDirectoryEntries(dirReader).then(async (entries) => {
                for (const entry of entries) {
                    const files = await traverseFileTree(entry, path + item.name + "/")
                    collectedFiles.push(...files)
                }
                resolve(collectedFiles)
            })
        }
    })
}

export default React.memo(function UploadDropBox(props: UploadDropBoxProps) {
    const {
        allowedFileExtensions,
        newItemsHandler,
        acceptMultiple,
        // this forces the upload field to be reset whenever the component re-renders
        // works for current use cases (external reset button) by adding Date.now() to the component
        resetTrigger = "static-field-key-will-never-reset",
        uploadRef,
        error,
        mapExtensionsToMimeType,
        theme = "dark",
        separator = ", ",
        dropFileExtensions,
        externalFileInputRef,
        showButton,
        maxUploads,
    } = props

    const [fileUploadError, setFileUploadError] = useState<boolean>(false)
    const { t } = useTranslation()
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [{ isOver }, drop] = useDrop<FileDropPayload, DropReturnType, CollectedDropProps>(
        dropConfigurationHandler(
            newItemsHandler,
            allowedFileExtensions,
            setFileUploadError,
            mapExtensionsToMimeType,
            dropFileExtensions,
            maxUploads
        ),
        [props]
    )

    const fileDialogTrigger = useCallback(() => {
        if (externalFileInputRef?.current) {
            externalFileInputRef.current.click()
        } else if (fileInputRef.current) {
            fileInputRef.current.click()
        }
    }, [fileInputRef, externalFileInputRef])

    const inputOnChangeHandler = (event: ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(get(event, "target.files", [])).map((file) => file) as File[]
        const allowed = fileAllowableCheck(files, allowedFileExtensions, mapExtensionsToMimeType)

        setFileUploadError(!allowed)
        if (!allowed) return

        newItemsHandler(files)
    }

    const targetClasses = classNames("upload-dropbox__target min-h-[120px]", {
        "upload-dropbox__target__border-white": isOver && !fileUploadError,
        "upload-dropbox__target__border-error": fileUploadError,
    })

    const hideWithoutResize = {
        "upload-dropbox__hidden": isOver && !fileUploadError,
    }
    const instructionsClasses = classNames("upload-dropbox__instructions", hideWithoutResize)
    const constraintsClasses = classNames("upload-dropbox__constraints", {
        "upload-dropbox__hidden": isOver,
    })
    const errorClasses = classNames("upload-dropbox__error")

    const formatFileExtensions = () => allowedFileExtensions.join(separator)
    const dropRef = useRef<HTMLDivElement>(null)
    drop(dropRef)

    return (
        <div
            ref={uploadRef}
            className={`upload-dropbox__container relative min-h-[120px] w-full h-full upload-dropbox__theme-${theme}`}
        >
            <div ref={dropRef} className={targetClasses}>
                <div className={instructionsClasses}>
                    {showButton && (
                        <div
                            onClick={fileDialogTrigger}
                            className="upload-dropbox__upload-button"
                            data-testid="upload-files-button"
                        >
                            {t(acceptMultiple ? "add_files" : "upload_file")}
                        </div>
                    )}

                    <div>
                        {t(
                            showButton
                                ? "or_drag_and_drop"
                                : `Drag and drop to add ${maxUploads === 1 ? "a " : ""}file${maxUploads === 1 ? "" : "s"} here`
                        )}
                    </div>

                    <input
                        data-tid="file-input"
                        data-testid="file-input"
                        ref={externalFileInputRef || fileInputRef}
                        key={resetTrigger}
                        id="file-upload-input"
                        name="file-upload"
                        type="file"
                        multiple={acceptMultiple}
                        className="hidden"
                        onChange={inputOnChangeHandler}
                        accept={getInputAcceptAttributeForExtensionsList(allowedFileExtensions)}
                    />
                </div>

                <div className={constraintsClasses}>
                    {t("valid_file_types")}
                    <strong className="upload-dropbox__valid-types">
                        {formatFileExtensions()}
                    </strong>
                </div>

                {(fileUploadError || error) && (
                    <div className={errorClasses}>{t(error || "File not allowed")}</div>
                )}

                {isOver && (
                    <div className="upload-target-drop-reminder absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex flex-col place-items-center">
                        <SimpleSmiley />

                        <span className="mt-1">{t("release_to_stage")}</span>
                    </div>
                )}
            </div>
        </div>
    )
})
